import React, { useState, useContext, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import IconButton from "@mui/material/IconButton";
import { useNavigate } from "react-router-dom";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import { logoWhite, logoSingle, logoutIcon, plus, minus } from "assets/icons";
import TextWithTooltip from "components/common/TextWithTooltip";
import ActionDialog from "components/common/ActionDialog";
import ClientContext from "../Context";
import usePageLogic from "./usePageLogic";
import EditMenuModal from "./EditMenuModal/index";
import { getIconUtil } from "../../../../utils/utils";
import { iconMap } from "assets/icons/iconMap";
import clsx from "clsx";
import "./styles.scss";

import {
  useManageExternalLinkMutation,
  useGetDashboardInfoMutation,
  useCreateDashboardMutation,
  useRemoveDashboardMutation,
} from "services/dashboard";
import {
  useCreateMenuMutation,
  useUpdateDashboardMutation,
} from "services/parentMenu";
import {
  useCreateMenuMutation as createSubMenuContext,
  useUpdateSubMenuMutation,
} from "services/submenus";

type MenuItem = {
  id: number | string;
  name: string;
  icon: string[];
  updated: boolean;
  submenus: {
    id: number | string;
    name: string;
    dashboard_id: number | string | null;
    link: string;
    updated: boolean;
  }[];
};

type UpdatedMenu = {
  id: number;
  hidden: boolean;
  order: number;
  name: string;
  editor_id: number;
  icons: string;
};

type UpdatedSubMenu = {
  sectionID: number;
  dashboardID: number;
  mainMenuID: number;
  hidden: boolean;
  order: number;
  name: string;
  editor_id: number;
};

type UpdatedDashboard = {
  name: string;
  link: string;
  is_hidden: boolean;
};

const Drawer: React.FC = () => {
  const {
    parentMenu,
    companyId,
    isEditMode,
    setParentMenu, // Assuming this function updates the menu order
    isSidebarCollapsed,
    setIsSidebarCollapsed,
  } = useContext(ClientContext);

  const navigate = useNavigate();

  const [activeMenu, setActiveMenu] = useState<number | string | null>(null);
  const [activeMockUpMenu, setActiveMockUpMenu] = useState<boolean>(false);
  const [isPinned, setIsPinned] = useState(false);
  const [submenuPosition, setSubmenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [currentMenu, setCurrentMenu] = useState<number | string | null>(null);
  const [currentSebMenu, setcurrentSebMenu] = useState<number | string | null>(
    null
  );
  const [isHoveredSubMenu, setIsHoveredSubMenu] = useState<
    number | string | null
  >(null);
  const [deletedItems, setDeletedItems] = useState<any[]>([]);
  const showDemo = false;

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState<MenuItem | null>(null);
  const [errors, setErrors] = useState<any>({});

  const [createNewDashboard] = useCreateDashboardMutation();
  const [removeDashboard] = useRemoveDashboardMutation();
  const [updatedParentMenu] = useUpdateDashboardMutation();
  const [updatedSubMenu] = useUpdateSubMenuMutation();
  const [manageExternalLink] = useManageExternalLinkMutation();

  const {
    setOpenLogoutDialog,
    userName,
    logout,
    openLogoutDialog,
    collapseSidebar,
  } = usePageLogic();

  const submenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        submenuRef.current &&
        !submenuRef.current.contains(event.target as Node)
      ) {
        setActiveMenu(null);
        setSubmenuPosition(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (
      parentMenu.length > 0 &&
      parentMenu[0].submenus.length > 0 &&
      parentMenu[0]?.id &&
      parentMenu[0]?.submenus[0]?.id &&
      parentMenu[0]?.submenus[0]?.dashboard_id
    ) {
      const menuId = parentMenu[0].id;
      const subMenuId = parentMenu[0].submenus[0].id;
      const dashboardId = parentMenu[0].submenus[0].dashboard_id;
      initalMenuState(dashboardId, menuId, subMenuId);
    } else if (
      parentMenu.length > 0 &&
      parentMenu[1].submenus.length > 0 &&
      parentMenu[1]?.id &&
      parentMenu[1]?.submenus[0]?.id &&
      parentMenu[1]?.submenus[0]?.dashboard_id
    ) {
      const menuId = parentMenu[1].id;
      const subMenuId = parentMenu[1].submenus[0].id;
      const dashboardId = parentMenu[1].submenus[0].dashboard_id;
      initalMenuState(dashboardId, menuId, subMenuId);
    } else if (
      parentMenu.length > 0 &&
      parentMenu[2].submenus.length > 0 &&
      parentMenu[2]?.id &&
      parentMenu[2]?.submenus[0]?.id &&
      parentMenu[2]?.submenus[0]?.dashboard_id
    ) {
      const menuId = parentMenu[2].id;
      const subMenuId = parentMenu[2].submenus[0].id;
      const dashboardId = parentMenu[2].submenus[0].dashboard_id;
      initalMenuState(dashboardId, menuId, subMenuId);
    }
  }, [parentMenu]);

  const toggleMenu = (
    menuId: number | string,
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    if (activeMenu === menuId) {
      setActiveMenu(null);
      setSubmenuPosition(null);
    } else {
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      setSubmenuPosition({ top: rect.bottom - 40, left: 265 });
      setActiveMenu(menuId);
      setIsPinned(true);
    }
  };

  const togglePin = () => {
    setIsPinned((prev) => !prev);
  };

  const openEditModal = (menu: MenuItem) => {
    setSelectedMenu(menu);
    setEditModalOpen(true);
  };

  const updatedParentMenuFun = async (
    data: UpdatedMenu
  ): Promise<number | Error> => {
    try {
      const resp: any = await updatedParentMenu({ data });

      if (resp.hasOwnProperty("error")) {
        setErrors("Failed to update parentMenu.");
        throw new Error("Failed to update parentMenu.");
      }

      return resp;
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

  const updatedSubMenuFun = async (
    data: UpdatedSubMenu
  ): Promise<number | Error> => {
    try {
      const resp: any = await updatedSubMenu({ data });

      if (resp.hasOwnProperty("error")) {
        setErrors("Failed to update subMenu.");
        throw new Error("Failed to update subMenu.");
      }

      return resp;
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

  const updatedDashboardFunc = async (
    data: UpdatedDashboard,
    companyId: Number,
    dashboardId: Number
  ): Promise<number | Error> => {
    try {
      const resp: any = await manageExternalLink({
        data,
        companyId,
        dashboardId,
      });

      if (resp.hasOwnProperty("error")) {
        setErrors("Failed to update dashboard.");
        throw new Error("Failed to update dashboard.");
      }
      return resp;
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

  const removeDashboardFunc = async (
    companyId: Number,
    dashboardId: Number
  ): Promise<number | Error> => {
    try {
      const resp: any = await removeDashboard({ companyId, dashboardId });

      if (resp.hasOwnProperty("error")) {
        setErrors("Failed to remove dashboard.");
        throw new Error("Failed to remove dashboard.");
      }
      return resp;
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

  const filterUpdatedItems = (items: MenuItem[]) => {
    return items.reduce((acc: MenuItem[], parent) => {
      if (parent.updated) {
        const updatedSubmenus = parent.submenus.filter(
          (submenu) => submenu.updated
        );

        if (updatedSubmenus.length > 0) {
          acc.push({ ...parent, submenus: updatedSubmenus });
        } else {
          acc.push({
            id: parent.id,
            name: parent.name,
            icon: parent.icon,
            updated: parent.updated,
            submenus: [],
          });
        }
      }
      return acc;
    }, []);
  };

  const arrayToString = (arr: string[]): string => {
    return `[${arr.join(", ")}]`;
  };

  const deleteDashboardItems = (items: any[]) => {
    items.forEach((item) => {
      removeDashboardFunc(Number(companyId), Number(item));
    });
  };

  const updatedParentsMenusDefaultMenu = (itemsUpdated: MenuItem[]) => {
    itemsUpdated.forEach((item) => {
      if (item.submenus && item.submenus.length > 0) {
        item.submenus.forEach((submenu) => {
          const dashboardUpdatedMenu = {
            name: submenu.name,
            link: submenu.link,
            is_hidden: false,
          };

          updatedDashboardFunc(
            dashboardUpdatedMenu,
            Number(companyId),
            Number(submenu.dashboard_id)
          );
        });
      }
    });
  };

  const updatedParentsMenus = (itemsUpdated: MenuItem[]) => {
    itemsUpdated.forEach((item) => {
      const itemUpdatedMenu = {
        id: Number(item.id),
        hidden: false,
        order: 0,
        name: item.name,
        editor_id: 1,
        icons: arrayToString(item.icon),
      };
      updatedParentMenuFun(itemUpdatedMenu);

      if (item.submenus && item.submenus.length > 0) {
        item.submenus.forEach((submenu) => {
          const subUpdatedMenu = {
            sectionID: Number(submenu.id),
            dashboardID: Number(submenu.dashboard_id),
            mainMenuID: Number(item.id),
            hidden: false,
            order: 0,
            name: submenu.name,
            editor_id: 1,
          };

          updatedSubMenuFun(subUpdatedMenu);

          const dashboardUpdatedMenu = {
            name: submenu.name,
            link: submenu.link,
            is_hidden: false,
          };

          updatedDashboardFunc(
            dashboardUpdatedMenu,
            Number(companyId),
            Number(submenu.dashboard_id)
          );
        });
      }
    });
  };

  const saveMenuChangesDefault = async (updatedMenu: MenuItem) => {
    const updatedMenus = parentMenu.map((menu) => {
      if (menu.id === updatedMenu.id) {
        return {
          ...updatedMenu,
        };
      }
      return menu;
    });

    const itemsUpdated = filterUpdatedItems(updatedMenus);
    deletedItems?.length > 0 && (await deleteDashboardItems(deletedItems));
    updatedParentsMenusDefaultMenu(itemsUpdated);
    setParentMenu(updatedMenus);
  };

  const saveMenuChanges = (updatedMenu: MenuItem) => {
    const updatedMenus = parentMenu.map((menu) => {
      if (menu.id === updatedMenu.id) {
        const selectedBlackIcon = updatedMenu.icon[0]; // Get the first icon (black icon)
        const correspondingWhiteIcon =
          iconMap[selectedBlackIcon] || selectedBlackIcon; // Get the white equivalent

        return {
          ...updatedMenu,
          icon: [selectedBlackIcon, correspondingWhiteIcon], // Always update both icons
        };
      }
      return menu;
    });
    const itemsUpdated = filterUpdatedItems(updatedMenus);
    updatedParentsMenus(itemsUpdated);
    setParentMenu(updatedMenus);
  };

  const removeItem = (dashboardId: Number) => {
    setDeletedItems((prev) => [...prev, dashboardId]);
  };

  const getInitials = (userName: string): string => {
    if (!userName) return "";

    const nameParts = userName.trim().split(" ");

    const firstInitial = nameParts[0]?.[0] || "";
    const secondInitial = nameParts.length > 1 ? nameParts[1]?.[0] || "" : "";

    return (firstInitial + secondInitial).toUpperCase().substring(0, 2);
  };

  const selectItemMenu = (
    menuId: number | string,
    submenuId: number | string,
    dashboard_id: number | string | null
  ) => {
    navigate(`/user/company/${companyId}/${dashboard_id}`);
    setCurrentMenu(menuId);
    setcurrentSebMenu(submenuId);
    setIsPinned(false);
    setSubmenuPosition(null);
  };

  const initalMenuState = (
    dashboard_id: number | string | null,
    menuId: number | string,
    submenuId: number | string
  ) => {
    navigate(`/user/company/${companyId}/${dashboard_id}`);
    setCurrentMenu(menuId);
    setcurrentSebMenu(submenuId);
  };

  const renderSubmenu = () => {
    if (!activeMenu || !submenuPosition) return null;

    const mockUpItem = {
      id: 100000004,
      name: "Lab Feature",
      icon: ["boxBlack", "box"],
      updated: false,
      submenus: [
        {
          order_index: 0,
          name: "Inventory Opportunity",
          is_hidden: false,
          link: "https://stg-api.intuilize.com/hi-ee/hi.html?dir=1726636313315&file=cf7d73fe-8f6b-4bea-b037-d274e929c7e0.efw&mode=dashboard",
          id: "214",
          dashboard_id: "214",
          is_external_dashboard: true,
          updated: false,
        },
        {
          order_index: 1,
          name: "Inventory Forecast",
          is_hidden: false,
          link: "https://stg-api.intuilize.com/hi-ee/hi.html?dir=1726636313315&file=cf7d73fe-8f6b-4bea-b037-d274e929c7e0.efw&mode=dashboard",
          id: "215",
          dashboard_id: "215",
          is_external_dashboard: true,
          updated: false,
        },
        {
          order_index: 2,
          name: "Inventory SKU Simulation",
          is_hidden: false,
          link: "https://stg-api.intuilize.com/hi-ee/hi.html?dir=1726636313315&file=cf7d73fe-8f6b-4bea-b037-d274e929c7e0.efw&mode&mode=dashboard",
          id: "149",
          dashboard_id: "149",
          is_external_dashboard: true,
          updated: false,
        },
      ],
    };
    let updatedParentMenu = [];

    if (companyId == "53") {
      updatedParentMenu = [...parentMenu, mockUpItem];
    } else {
      updatedParentMenu = [...parentMenu];
    }

    const activeItem = updatedParentMenu.find((item) => item.id === activeMenu);
    if (!activeItem) return null;

    if (activeItem.submenus.length <= 0) return null;

    return ReactDOM.createPortal(
      <div
        ref={submenuRef}
        style={{
          top: submenuPosition.top,
          left: isSidebarCollapsed
            ? submenuPosition.left - 180
            : submenuPosition.left,
          position: "absolute",
          background: "#123658",
          padding: "15px 10px",
          borderRadius: "6px",
          zIndex: "10",
        }}
        className="submenu-popup"
      >
        <ul>
          {[...activeItem.submenus]
            .filter((submenu) => {
              return !(submenu.is_hidden === true);
            })
            .sort((a, b) => {
              const aOrder = (a as any)?.order_index;
              const bOrder = (b as any)?.order_index;

              const aHasIndex = typeof aOrder === "number";
              const bHasIndex = typeof bOrder === "number";

              if (!aHasIndex && !bHasIndex) return 0;
              if (!aHasIndex) return -1;
              if (!bHasIndex) return 1;

              return aOrder - bOrder;
            })
            .map((submenu, index) => (
              <li
                key={index}
                className="submenu-item"
                style={{
                  marginTop: index === 0 ? "0" : "5px",
                  padding: activeMenu === 100000004 ? "16px" : "8px 16px",
                  color:
                    currentSebMenu === submenu.id &&
                    currentMenu === activeItem.id
                      ? "#123658"
                      : "#e8f1f4",
                  backgroundColor:
                    currentSebMenu === submenu.id &&
                    currentMenu === activeItem.id
                      ? "#dde44d"
                      : isHoveredSubMenu === submenu.id
                      ? "#153E65"
                      : "transparent",
                  fontFamily: "Rubik-Regular",
                  fontSize: "14px",
                  cursor: "pointer",
                  borderRadius: "5px",
                  border:
                    activeMenu === 100000004 ? "1px dashed white" : "none",
                }}
                onClick={() =>
                  selectItemMenu(
                    activeItem.id,
                    submenu.id,
                    submenu.dashboard_id
                  )
                }
                onMouseEnter={() => setIsHoveredSubMenu(submenu.id)}
                onMouseLeave={() => setIsHoveredSubMenu(null)}
              >
                <span
                  className="menu-label"
                  style={{
                    marginRight: activeMenu === 100000004 ? "15px" : "0px",
                  }}
                >
                  {submenu.name}
                </span>
                {activeMenu === 100000004 && (
                  <img
                    src={
                      submenu.name === "Inventory SKU Simulation"
                        ? getIconUtil("padlock")
                        : getIconUtil("matraz")
                    }
                    style={{
                      width: "25px",
                      height: "25px",
                      marginLeft: "16px",
                    }}
                    alt="MockupMenu"
                    className="menu-icon-2"
                  />
                )}
              </li>
            ))}
        </ul>
      </div>,
      document.body
    );
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const reorderedMenu = Array.from(parentMenu);
    const [movedItem] = reorderedMenu.splice(result.source.index, 1);
    reorderedMenu.splice(result.destination.index, 0, movedItem);

    setParentMenu(reorderedMenu);
  };

  return (
    <div
      className={`client-drawer ${
        isSidebarCollapsed ? "collapse-sidebar" : ""
      }`}
    >
      <div className="drawer-container">
        {/* <div className="image-container">
          <img className="intuilize-logo" src={logoWhite} alt="logo-light" />
        </div> */}
        <div
          className={clsx("ctn-logo", {
            "ctn-logo-collapsed": isSidebarCollapsed,
          })}
        >
          {isSidebarCollapsed ? (
            <img
              className="intuilize-logo intuilize-logo-single"
              src={logoSingle}
              alt="logo-light"
            />
          ) : (
            <img className="intuilize-logo" src={logoWhite} alt="logo-light" />
          )}
        </div>
        <nav className="client-navigation">
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="menu-list">
              {(provided) => (
                <ul {...provided.droppableProps} ref={provided.innerRef}>
                  {parentMenu
                    .filter((item) => item.submenus.length > 0)
                    .map((item, index) => (
                      <Draggable
                        key={item.id}
                        draggableId={String(item.id)}
                        index={index}
                      >
                        {(provided) => (
                          <li
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="menu-item"
                          >
                            <div
                              className={`${
                                currentMenu === item.id
                                  ? "menu-item-header-selected"
                                  : "menu-item-header"
                              } ${
                                isSidebarCollapsed
                                  ? "menu-item-headerCollapsed"
                                  : ""
                              }`}
                              onClick={
                                isEditMode
                                  ? () => openEditModal(item)
                                  : (event) => {
                                      togglePin();
                                      toggleMenu(item.id, event);
                                    }
                              }
                            >
                              {isEditMode && (
                                <DragIndicatorIcon
                                  style={{
                                    cursor: "grab",
                                    marginRight: "8px",
                                    color: "#ccc",
                                  }}
                                />
                              )}
                              <img
                                src={
                                  currentMenu === item.id
                                    ? getIconUtil(item.icon[0])
                                    : getIconUtil(item.icon[1])
                                }
                                alt={`${item.name} icon`}
                                className="menu-icon"
                              />
                              {!isSidebarCollapsed && (
                                <span className="menu-label">{item.name}</span>
                              )}
                            </div>
                          </li>
                        )}
                      </Draggable>
                    ))}

                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
          </DragDropContext>
          {companyId == "53" && showDemo && (
            <div>
              <ul>
                <li
                  className="menu-item"
                  style={{
                    border: "1px dashed white",
                    padding: "8px",
                  }}
                >
                  <div
                    className="menu-item-header"
                    onClick={(event) => {
                      togglePin();
                      toggleMenu(100000004, event);
                    }}
                  >
                    {isSidebarCollapsed && (
                      <img
                        src={getIconUtil("matraz")}
                        alt="MockupMenu"
                        className="menu-icon-3"
                      />
                    )}
                    {!isSidebarCollapsed && (
                      <span
                        className="menu-label"
                        style={{
                          marginRight: "15px",
                        }}
                      >
                        Lab Feature
                      </span>
                    )}
                    {!isSidebarCollapsed && (
                      <img
                        src={getIconUtil("matraz")}
                        alt="MockupMenu"
                        className="menu-icon-2"
                      />
                    )}
                  </div>
                </li>
              </ul>
            </div>
          )}
        </nav>
      </div>

      {!isEditMode && (
        <div
          className={
            !isSidebarCollapsed
              ? "floating-collapse-button"
              : "floating-collapse-buttonCollapsed"
          }
          onClick={() => collapseSidebar()}
        >
          {isSidebarCollapsed ? "›" : "‹"}
        </div>
      )}

      <div className="drawer-tab">
        {/* <div className="profile-tab">
          <TextWithTooltip text={userName} className="tab-content user-name" />
          <IconButton
            aria-label="logout"
            className="logout-icon"
            onClick={() => setOpenLogoutDialog(true)}
          >
            <img src={logoutIcon} alt="logout" />
          </IconButton>
        </div> */}

        <div className="drawer-tab profile-tab">
          <TextWithTooltip
            text={isSidebarCollapsed ? getInitials(userName) : userName}
            className="tab-content"
          />
          <IconButton
            aria-label="logout"
            onClick={() => setOpenLogoutDialog(true)}
            className="logout-icon"
          >
            <img src={logoutIcon} alt="logout" />
          </IconButton>
        </div>
      </div>

      {renderSubmenu()}

      <ActionDialog
        onClose={() => setOpenLogoutDialog(false)}
        onApply={logout}
        open={openLogoutDialog}
        ids={{
          dialogTitle: "logOut",
          dialogContent: "confirmLogOut",
        }}
      />

      {/* Render the Edit Menu Modal */}
      <EditMenuModal
        open={editModalOpen}
        handleClose={() => setEditModalOpen(false)}
        menu={selectedMenu}
        saveChanges={saveMenuChanges}
        saveChangesDefaultMenu={saveMenuChangesDefault}
        removeItem={removeItem}
      />
    </div>
  );
};

export default Drawer;
